import {
  createRouter,
  createWebHistory,
  RouteLocationNormalizedGeneric,
} from 'vue-router'
import { createAuthGuard } from '@/src/shared/auth/routerGuard'
import { useUserProfileStore } from '@/buyer/store/userProfile'
import { refRoutes } from '@/src/ref/ref-router'
import { fsmRoutes } from '@/src/fsm/fsm-router'

const LoginForm = () => import('../../components/auth/LoginForm.vue')
const ForgotPassword = () => import('../../components/auth/ForgotPassword.vue')
const ResetPassword = () => import('../../components/auth/ResetPassword.vue')
const AuditEvents = () => import('../../components/audit/AuditEvents.vue')
const CategoryEdit = () => import('../components/category/CategoryEdit.vue')
const CategoryList = () => import('../components/category/CategoryList.vue')
const CategoryNew = () => import('../components/category/CategoryNew.vue')
const Component404 = () => import('../../components/404.vue')
const Issue = () => import('../components/issue/IssueLayout.vue')
const IssueDetail = () => import('../components/issue/IssueDetail.vue')
const IssueList = () => import('../components/issue/IssueList.vue')
const ParticipantList = () =>
  import('../components/project/participants/ParticipantList.vue')
const ParticipantForm = () =>
  import('../components/project/participants/ParticipantForm.vue')
const ProjectAttachments = () =>
  import('../components/project/attachments/ProjectAttachments.vue')
const ProjectAttachmentsAdd = () =>
  import('../components/project/attachments/ProjectAttachmentsAdd.vue')
const ProjectDetails = () => import('../components/project/ProjectDetails.vue')
const ProjectFindReplace = () =>
  import('../components/project/ProjectFindReplace.vue')
const ProjectLayout = () => import('../components/ProjectLayout.vue')
const ProjectMessages = () =>
  import('../components/project/ProjectMessages.vue')
const ProjectNew = () => import('../components/project/ProjectNew.vue')
const ProjectReports = () => import('../components/report/ProjectReports.vue')
const ProjectSettings = () =>
  import('../components/project/ProjectSettings.vue')
const ProjectStatus = () => import('../components/project/ProjectStatus.vue')
const ProjectUsers = () => import('../components/project/ProjectUsers.vue')
const ProjectApprovals = () =>
  import('../components/project/ProjectApprovals.vue')
const ProjectList = () => import('../components/ProjectList.vue')
const ResponseAttachments = () =>
  import('../components/project/attachments/ResponseAttachments.vue')
const RootLayout = () => import('../components/RootLayout.vue')
const Search = () => import('../components/Search.vue')
const SectionLayout = () =>
  import('../components/questionnaire/SectionLayout.vue')
const SectionContents = () =>
  import('../components/section/SectionContents.vue')
const SectionWeightings = () =>
  import('../components/section/SectionWeightings.vue')
const SectionScores = () => import('../components/section/SectionScores.vue')
const SectionScoreList = () =>
  import('../components/section/scores/ScoreList.vue')
const ChartContainer = () =>
  import('../components/questionnaire/chart/ChartContainer.vue')
const SectionTags = () => import('../components/section/SectionTags.vue')
const SectionIssueScores = () =>
  import('../components/section/scores/IssueScores.vue')
const NetworkChartManager = () =>
  import('../components/network/NetworkChartManager.vue')
const QuestionnaireLayout = () =>
  import('../components/questionnaire/QuestionnaireLayout.vue')
const QuestionLayout = () =>
  import('../components/questionnaire/QuestionLayout.vue')
const QuestionEditing = () =>
  import('../components/question/QuestionEditing.vue')
const QuestionScores = () => import('../components/question/QuestionScores.vue')
const QuestionScoreList = () =>
  import('../components/question/scores/ScoreList.vue')
const QuestionScoreForm = () =>
  import('../components/question/scores/ScoreForm.vue')
const Webhooks = () => import('../components/webhooks/Webhooks.vue')
const UserList = () => import('../components/user/UserList.vue')
const UserForm = () => import('../components/user/UserForm.vue')
const WeightsetList = () =>
  import('../components/project/weightsets/WeightsetList.vue')
const WeightsetForm = () =>
  import('../components/project/weightsets/WeightsetForm.vue')
const TagList = () => import('../components/tags/TagList.vue')
const OrganisationList = () =>
  import('../components/organisation/OrganisationList.vue')
const OrganisationForm = () =>
  import('../components/organisation/OrganisationForm.vue')
const ResponsesList = () => import('../components/responses/ResponsesList.vue')
const Theme = () => import('../components/theme/Theme.vue')

const Dashboard = () => import('../components/Dashboard.vue')

const projectIdProps = (route: RouteLocationNormalizedGeneric) => ({
  projectId: Number(route.params.projectId),
})

const projectIdSectionIdProps = (route: RouteLocationNormalizedGeneric) => ({
  projectId: Number(route.params.projectId),
  sectionId: Number(route.params.sectionId),
})

const projectIdSectionIdQuestionIdProps = (
  route: RouteLocationNormalizedGeneric
) => ({
  projectId: Number(route.params.projectId),
  sectionId: Number(route.params.sectionId),
  questionId: Number(route.params.questionId),
})

const projectIdSectionIdQuestionIdIssueIdProps = (
  route: RouteLocationNormalizedGeneric
) => ({
  projectId: Number(route.params.projectId),
  sectionId: Number(route.params.sectionId),
  questionId: Number(route.params.questionId),
  issueId: Number(route.params.issueId),
})

const sectionPageProps = { sectionPage: projectIdProps }

const sectionTabProps = {
  sectionTab: projectIdSectionIdProps,
}

const questionnaireViewProps = {
  questionnaireView: projectIdSectionIdProps,
}

const questionTabProps = {
  questionTab: projectIdSectionIdQuestionIdProps,
}

const router = createRouter({
  history: createWebHistory('/buyer/'),
  routes: [
    {
      path: '/login',
      name: 'Login',
      alias: '/logout',
      component: LoginForm,
      props: {
        appType: 'buyer',
        defaultRedirect: 'projects',
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
    },
    {
      path: '/reset',
      name: 'reset-password',
      component: ResetPassword,
      props: (route) => ({
        key: route.query.key,
        user: route.query.user,
      }),
    },
    {
      path: '',
      component: RootLayout,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          redirect: 'projects',
          name: 'root',
        },
        {
          path: 'projects',
          name: 'projects',
          component: ProjectList,
          meta: {
            header: true,
          },
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: Dashboard,
          meta: {
            header: true,
          },
        },
        {
          path: 'search',
          name: 'globalSearch',
          component: Search,
          props: (route) => ({
            default: true,
            isGlobalSearch: true,
            searchTerm: route.query.q,
            searchOption: route.query.opt,
          }),
          meta: {
            header: true,
          },
        },
        {
          path: 'events',
          name: 'globalAuditEvents',
          component: AuditEvents,
          meta: {
            header: true,
          },
        },
        {
          path: 'proj/new',
          name: 'newProject',
          component: ProjectNew,
          meta: {
            header: true,
          },
        },
        {
          path: 'categories',
          name: 'categories',
          component: CategoryList,
          meta: {
            header: true,
          },
        },
        {
          path: 'categories/new',
          name: 'newCategory',
          component: CategoryNew,
        },
        {
          path: 'categories/edit/:categoryId',
          name: 'editCategory',
          component: CategoryEdit,
          props: ({ params }) => ({
            categoryId: Number(params.categoryId),
          }),
        },
        {
          path: 'webhooks',
          name: 'webhooks',
          component: Webhooks,
          meta: {
            header: true,
          },
        },
        {
          path: 'theme',
          name: 'theme',
          component: Theme,
          meta: {
            header: true,
          },
        },
        {
          path: 'users',
          name: 'users',
          component: UserList,
          props: true,
          meta: {
            requiresAuth: true,
            header: true,
          },
        },
        {
          path: 'users/:mode/:userId?',
          name: 'userForm',
          component: UserForm,
          props: true,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: 'tags',
          name: 'tags',
          component: TagList,
          meta: {
            header: true,
          },
        },
        {
          path: 'organisations',
          name: 'organisations',
          component: OrganisationList,
          meta: {
            requiresAuth: true,
            permissions: ['manageOrganisation'],
            header: true,
          },
        },
        {
          path: 'organisations/:orgId',
          name: 'organisationForm',
          component: OrganisationForm,
          props: true,
          meta: {
            requiresAuth: true,
            permissions: ['manageOrganisation'],
            header: true,
          },
        },
        {
          path: 'responses',
          name: 'responses',
          component: ResponsesList,
          meta: {
            header: true,
          },
        },
        ...refRoutes,
        ...fsmRoutes,
      ],
    },
    {
      path: '/proj/:projectId',
      component: ProjectLayout,
      props: projectIdProps,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'projectDetails',
          components: { sectionPage: ProjectDetails },
          props: sectionPageProps,
        },
        {
          path: 'respondents',
          name: 'respondent',
          components: { sectionPage: Issue },
          props: sectionPageProps,
          children: [
            {
              path: '',
              name: 'issueList',
              components: { issueView: IssueList },
              props: {
                issueView: (route) => ({
                  projectId: Number(route.params.projectId),
                }),
              },
            },
            {
              path: ':mode/:issueId?',
              name: 'issueDetail',
              components: { issueView: IssueDetail },
              props: {
                issueView: (route) => ({
                  mode: route.params.mode,
                  projectId: Number(route.params.projectId),
                  issueId: Number(route.params.issueId),
                }),
              },
            },
          ],
        },
        {
          path: 'messages',
          name: 'messages',
          components: { sectionPage: ProjectMessages },
          props: sectionPageProps,
        },
        {
          path: 'participants',
          name: 'participants',
          components: { sectionPage: ParticipantList },
          props: sectionPageProps,
        },
        {
          path: 'approvals',
          name: 'projectApprovals',
          components: { sectionPage: ProjectApprovals },
          props: sectionPageProps,
        },
        {
          path: 'participants/:mode/:orgId?',
          name: 'participantForm',
          components: { sectionPage: ParticipantForm },
          props: {
            sectionPage: (route) => ({
              projectId: Number(route.params.projectId),
              mode: route.params.mode,
              orgId: route.params.orgId,
            }),
          },
        },
        {
          path: 'search',
          name: 'projectSearch',
          components: { sectionPage: Search },
          props: sectionPageProps,
        },
        {
          path: 'status',
          name: 'projectStatus',
          components: { sectionPage: ProjectStatus },
          props: sectionPageProps,
        },
        {
          path: 'findReplace',
          name: 'projectFindReplace',
          components: { sectionPage: ProjectFindReplace },
          props: sectionPageProps,
        },
        {
          path: 'projectAttachments',
          name: 'projectAttachments',
          components: { sectionPage: ProjectAttachments },
          props: sectionPageProps,
        },
        {
          path: 'projectAttachments/add',
          name: 'projectAttachmentsAdd',
          components: { sectionPage: ProjectAttachmentsAdd },
          props: sectionPageProps,
        },
        {
          path: 'responseAttachments',
          name: 'responseAttachments',
          components: { sectionPage: ResponseAttachments },
          props: sectionPageProps,
        },
        {
          path: 'settings',
          name: 'projectSettings',
          components: { sectionPage: ProjectSettings },
          props: sectionPageProps,
        },
        {
          path: 'users',
          name: 'projectUsers',
          components: { sectionPage: ProjectUsers },
          props: sectionPageProps,
        },
        {
          path: 'questionnaire',
          name: 'questionnaire',
          components: { sectionPage: QuestionnaireLayout },
          props: sectionPageProps,
          children: [
            {
              path: 's/:sectionId',
              name: 'section',
              components: { questionnaireView: SectionLayout },
              // redirect: { name: 'sectionContents'},
              props: questionnaireViewProps,
              children: [
                {
                  path: '',
                  name: 'sectionContents',
                  components: { sectionTab: SectionContents },
                  props: sectionTabProps,
                },
                {
                  path: 'weightings',
                  name: 'sectionWeightings',
                  components: { sectionTab: SectionWeightings },
                  props: sectionTabProps,
                },
                {
                  path: 'chart',
                  name: 'sectionChart',
                  components: { sectionTab: ChartContainer },
                  props: sectionTabProps,
                },
                {
                  path: 'tags',
                  name: 'sectionTags',
                  components: { sectionTab: SectionTags },
                  props: sectionTabProps,
                },
                {
                  path: 'scores',
                  name: 'sectionScores',
                  components: { sectionTab: SectionScores },
                  redirect: { name: 'sectionScoreList' },
                  props: sectionTabProps,
                  children: [
                    {
                      path: '',
                      name: 'sectionScoreList',
                      components: { scoreView: SectionScoreList },
                      props: { ScoreView: projectIdProps },
                    },
                    {
                      path: 'issue/:issueId',
                      name: 'sectionIssueScores',
                      components: { scoreView: SectionIssueScores },
                      props: {
                        scoreView: (route) => ({
                          projectId: Number(route.params.projectId),
                          sectionId: Number(route.params.sectionId),
                          issueId: Number(route.params.issueId),
                        }),
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: 's/:sectionId/q/:questionId',
              name: 'question',
              components: { questionnaireView: QuestionLayout },
              // redirect: { name: 'questionEdit'},

              props: {
                questionnaireView: (route) => ({
                  projectId: Number(route.params.projectId),
                  sectionId: Number(route.params.sectionId),
                  questionId: Number(route.params.questionId),
                }),
              },
              children: [
                {
                  path: '',
                  name: 'questionEdit',
                  components: { questionTab: QuestionEditing },
                  props: questionTabProps,
                },
                {
                  path: 'chart',
                  name: 'questionChart',
                  components: { questionTab: ChartContainer },
                  props: questionTabProps,
                },
                {
                  path: 'scores',
                  name: 'questionScores',
                  components: { questionTab: QuestionScores },
                  redirect: { name: 'questionScoreList' },
                  props: questionTabProps,
                  children: [
                    {
                      path: '',
                      name: 'questionScoreList',
                      components: { scoreView: QuestionScoreList },
                      props: { scoreView: projectIdSectionIdQuestionIdProps },
                    },
                    {
                      path: 'issue/:issueId',
                      name: 'questionScoreForm',
                      components: { scoreView: QuestionScoreForm },
                      props: {
                        scoreView: projectIdSectionIdQuestionIdIssueIdProps,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'events',
          name: 'projectEvents',
          components: { sectionPage: AuditEvents },
          props: sectionPageProps,
        },
        {
          path: 'reports',
          name: 'projectReports',
          components: { sectionPage: ProjectReports },
          props: sectionPageProps,
        },
        {
          path: 'weightsets',
          name: 'weightsets',
          components: { sectionPage: WeightsetList },
          props: sectionPageProps,
        },
        {
          path: 'weightsets/:mode/:weightsetId?',
          name: 'weightsetForm',
          components: { sectionPage: WeightsetForm },
          props: {
            sectionPage: (route: RouteLocationNormalizedGeneric) => ({
              projectId: Number(route.params.projectId),
              mode: route.params.mode,
              // Avoid NaN when the optional param is missing
              weightsetId: route.params.weightsetId
                ? Number(route.params.weightsetId)
                : undefined,
            }),
          },
        },
      ],
    },
    {
      path: '/network',
      name: 'network',
      component: NetworkChartManager,
      meta: {
        header: true,
        requiresAuth: true,
      },
    },
    {
      path: '/:patchMatch(.*)*',
      name: '404',
      component: Component404,
    },
  ],
})

// Apply the auth guard with buyer-specific configuration
router.beforeEach(
  createAuthGuard({
    redirectRoute: 'Login',
    authCheckFn: () => {
      const authStore = useUserProfileStore()
      return authStore.isLoggedIn
    },
  })
)

export default router
